import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Badge from "../../components/common/Badge/Badge";
import TabsNavigation from "../../components/common/TabsNavigation/TabsNavigation";
import Table from "../../components/common/Table/Table";
import ValidacionesHistorico from "../../components/common/ValidacionesHistorico/ValidacionesHistorico";
import "./FichaObraMenor.scss";
import DetalleLayout from "../../Layouts/DetalleLayout/DetalleLayout";
import { getApiInstance } from "../../utils/api";
import { CiCircleCheck, CiClock1 } from "react-icons/ci";
import { CgDanger } from "react-icons/cg";
import { addOrUpdateRecentItem, formatDate } from "../../utils/utils";

// Elementos del formulario para documentos
const getBaseFormElementsDocumentos = (t) => [
    {
        elements: [
            {
                name: "obs",
                type: "textarea",
                label: t("minorWorkSheet.description"),
                placeholder: t("minorWorkSheet.enterDocumentDescription"),
                required: true,
                disabled: false,
                labelVisible: true,
                visible: true,
            },
            {
                name: "fichero",
                type: "document",
                label: t("minorWorkSheet.document"),
                placeholder: "Subir documento",
                required: true,
                disabled: false,
                labelVisible: true,
                visible: true,
            },
        ],
    },
];

export default function FichaObraMenor({ user }) {
    const { t } = useTranslation();
    const { id } = useParams();
    const [obraMenor, setObraMenor] = useState(null);
    const [documentos, setDocumentos] = useState([]);
    const [activeTabGeneral, setActiveTabGeneral] = useState("informacion");
    const [activeTabDatos, setActiveTabDatos] = useState("documentacion");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isHistoricoOpen, setIsHistoricoOpen] = useState(false);
    const [documentoHistorico, setDocumentoHistorico] = useState(null);

    /* Mismas reglas que la aplicación antigua (Views/ObrasMenores/Details.cshtml):
       sobre una obra cerrada (MinorCenterState.CLOSED = 5) no se toca nada, validar
       es de administradores (perfiles 1 y 2) y el histórico lo ven además los
       intermediarios (perfil 4). */
    const puedeGestionar =
        Number(user?.codAcceso) === 1 && obraMenor?.codEstado !== 5;
    const puedeValidar =
        puedeGestionar &&
        (Number(user?.role) === 1 || Number(user?.role) === 2);
    const puedeVerValidaciones = [1, 2, 4].includes(Number(user?.role));

    const abrirHistorico = (row) => {
        setDocumentoHistorico(row);
        setIsHistoricoOpen(true);
    };

    useEffect(() => {
        if (obraMenor != null) {
            addOrUpdateRecentItem({
                id: obraMenor.codObraMenor,
                title: obraMenor.nombre,
                type: "obrasMenores",
            });
        }
    }, [obraMenor]);

    const fetchObraMenor = async (id) => {
        try {
            const api = getApiInstance();
            const response = await api.get(`/api/ObraMenor/GetObraMenor/${id}`);
            return await response.data;
        } catch (error) {
            console.error("Error fetching gestor:", error);
            throw error;
        }
    };

    const fetchDocumentosObraMenor = async (id) => {
        try {
            const api = getApiInstance();
            const response = await api.get(
                `/api/ObraMenorDocumento/GetDocumentosFromObraMenor/${id}`
            );
            return await response.data;
        } catch (error) {
            console.error("Error fetching gestor:", error);
            throw error;
        }
    };

    useEffect(() => {
        const loadObraMenorData = async () => {
            try {
                setLoading(true);
                setError(null);

                const results = await Promise.allSettled([
                    fetchObraMenor(id),
                    fetchDocumentosObraMenor(id),
                ]);

                if (results[0].status === "fulfilled") {
                    setObraMenor(results[0].value);
                } else {
                    console.error(
                        "Error cargando la obra menor:",
                        results[0].reason
                    );
                    setError(
                        t("minorWorkSheet.couldNotLoadTheMainInformationOf")
                    );
                }

                if (results[1].status === "fulfilled") {
                    setDocumentos(results[1].value);
                } else {
                    console.warn(
                        "No se encontraron documentos para la obra:",
                        results[1].reason
                    );
                }
            } catch (err) {
                console.error("Error cargando datos de la obra menor:", err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            loadObraMenorData();
        }
    }, [id]);

    const getEstadoBadge = (estado) => {
        const estadoConfig = {
            VALIDADO: { type: "success", label: t("minorWorkSheet.validated") },
            PENDIENTE: { type: "info", label: t("minorWorkSheet.pending") },
            INCIDENCIA: { type: "warning", label: t("minorWorkSheet.incident") },
            VENCIDO: { type: "error", label: t("minorWorkSheet.expired") },
            ACTIVA: { type: "success", label: t("minorWorkSheet.active") },
        };
        return estadoConfig[estado] || estadoConfig["PENDIENTE"];
    };

    const tabsGeneral = [
        { id: "informacion", label: t("minorWorkSheet.generalInformation") },
        { id: "localizacion", label: t("minorWorkSheet.location") },
    ];

    const tabsDatos = [{ id: "documentacion", label: t("minorWorkSheet.documentation") }];

    const formElementsDocumentos = getBaseFormElementsDocumentos(t).map(
        (grupo) => ({
            ...grupo,
            elements: [
                ...grupo.elements,
                {
                    name: "codObraMenor",
                    type: "number",
                    label: t("minorWorkSheet.minorWork"),
                    visible: false,
                    defaultValue: Number(id),
                },
            ],
        })
    );

    const renderInformacionGeneral = () => (
        <div className="ficha-gestor__info">
            <div className="ficha-gestor__field">
                <label>{t("minorWorkSheet.producerColon")}</label>
                <span>{obraMenor.productor}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("minorWorkSheet.workCodeColon")}</label>
                <span>{obraMenor.nombre}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("minorWorkSheet.startDateColon")}</label>
                <span>{formatDate(obraMenor.fechaInicio)}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("minorWorkSheet.endDateColon")}</label>
                <span>{formatDate(obraMenor.fechaFin)}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("minorWorkSheet.intermediaryCompanyColon")}</label>
                <span>{obraMenor.intermediario}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("minorWorkSheet.chainColon")}</label>
                <span>{obraMenor.cadena}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("minorWorkSheet.generatedHwQuantityKgColon")}</label>
                <span>{obraMenor.cantidadRP}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("minorWorkSheet.generatedNhwQuantityTnColon")}</label>
                <span>{obraMenor.cantidadRNP}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("minorWorkSheet.observationsColon")}</label>
                <span>{obraMenor.obs}</span>
            </div>
        </div>
    );

    const renderLocalizacion = () => (
        <div className="ficha-gestor__info">
            <div className="ficha-gestor__field">
                <label>{t("minorWorkSheet.addressColon")}</label>
                <span>{obraMenor.direccion}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("minorWorkSheet.localityColon")}</label>
                <span>{obraMenor.localidad}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("minorWorkSheet.provinceColon")}</label>
                <span>{obraMenor.provincia}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("minorWorkSheet.communityColon")}</label>
                <span>{obraMenor.comunidad}</span>
            </div>
            <div className="ficha-gestor__field">
                <label>{t("minorWorkSheet.countryColon")}</label>
                <span>{obraMenor.pais}</span>
            </div>
        </div>
    );

    const renderGeneralContent = () => {
        switch (activeTabGeneral) {
            case "informacion":
                return renderInformacionGeneral();
            case "localizacion":
                return renderLocalizacion();
            default:
                return renderInformacionGeneral();
        }
    };

    const renderDatosContent = () => {
        switch (activeTabDatos) {
            case "documentacion":
                return (
                    <div className="ficha-centro__documentacion">
                        <Table
                            columns={[
                                {
                                    key: "documento",
                                    label: t("minorWorkSheet.document"),
                                    visible: true,
                                    sortable: true,
                                    documentProp: "ficheroBase64",
                                    type: "document",
                                },
                                {
                                    key: "obs",
                                    label: t("minorWorkSheet.description"),
                                    visible: true,
                                    sortable: true,
                                },
                                {
                                    key: "codEstado",
                                    label: t("minorWorkSheet.validation"),
                                    visible: true,
                                    sortable: true,
                                    titleProp: "lastValidationNote",
                                },
                            ]}
                            dataTable={documentos}
                            noResultsMessage={t("minorWorkSheet.noDocumentsAvailable")}
                            rowsPerPage={10}
                            showCheckboxes={false}
                            showAdvancedFilters={false}
                            /* Sobre una obra menor cerrada no se suben documentos,
                               como en la aplicacion antigua. */
                            hideAddButton={!puedeGestionar}
                            codTipoValidacion={9}
                            actions={[
                                ...(puedeValidar
                                    ? [
                                          {
                                              label: t("minorWorkSheet.validate"),
                                              actionType: "validate",
                                              icon: <CiCircleCheck size={16} />,
                                          },
                                      ]
                                    : []),
                                ...(puedeVerValidaciones
                                    ? [
                                          {
                                              label: t("minorWorkSheet.viewValidations"),
                                              action: abrirHistorico,
                                              icon: <CiClock1 size={16} />,
                                          },
                                      ]
                                    : []),
                                ...(puedeGestionar ? ["Eliminar"] : []),
                            ]}
                            elementName={t("elements.document")}
                            keyProp="codDocumento"
                            sortProp="documento"
                            searchTerms={["documento", "obs"]}
                            stateProp="codEstado"
                            states={[
                                {
                                    label: t("minorWorkSheet.noData"),
                                    type: "info",
                                    Icon: CiClock1,
                                },
                                {
                                    label: t("minorWorkSheet.pendingValidation"),
                                    type: "info",
                                    Icon: CiClock1,
                                },
                                {
                                    label: t("minorWorkSheet.validationWithIncidents"),
                                    type: "danger",
                                    Icon: CgDanger,
                                },
                                {
                                    label: t("minorWorkSheet.validated2"),
                                    type: "success",
                                    Icon: CiCircleCheck,
                                },
                            ]}
                            stateChangeOptions={[
                                {
                                    value: "1",
                                    label: t("minorWorkSheet.pendingValidation"),
                                },
                                {
                                    value: "2",
                                    label: t("minorWorkSheet.validationWithIncidents"),
                                },
                                { value: "3", label: t("minorWorkSheet.validated2") },
                            ]}
                            /* Sin permiso para validar no se pasa la URL: así la tabla no
                               ofrece el cambio de estado ni el botón de validar en bloque. */
                            stateChangeUrl={
                                puedeValidar
                                    ? "/api/Validaciones/SetValidacion"
                                    : undefined
                            }
                            formElements={formElementsDocumentos}
                            addApiUrl="/api/ObraMenorDocumento/SetDocumento"
                            deleteApiUrl="/api/ObraMenorDocumento/DeleteDocumento"
                            successMessages={{
                                add: t("minorWorkSheet.documentAdded"),
                                edit: t("minorWorkSheet.documentEdited"),
                                delete: t("minorWorkSheet.documentDeleted"),
                            }}
                            onDataUpdate={async () => {
                                const data =
                                    await fetchDocumentosObraMenor(id);
                                setDocumentos(data);
                            }}
                            loading={loading}
                            userCodAcceso={Number(user.codAcceso)}
                            userCodPerfil={Number(user.role)}
                            /* El intermediario sube y borra documentos, como en
                               la antigua (ObrasMenores/Details.cshtml). */
                            intermediarioPuedeEscribir
                        />
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <DetalleLayout
            title={
                obraMenor
                    ? t("minorWorkSheet.titleN", {
                          name: obraMenor.nombre,
                          address: obraMenor.direccion,
                          locality: obraMenor.localidad,
                      })
                    : t("common.loading")
            }
            backLink="/obras-menores"
            backText={t("backTo.minorWorks")}
            loading={loading}
            error={error}
            notFound={!obraMenor && !loading && !error}
            className="ficha-gestor"
        >
            {!loading && !error && obraMenor && (
                <div className="ficha-gestor__content">
                    <div className="ficha-gestor__section">
                        <div className="ficha-gestor__section-header">
                            <h2>{t("minorWorkSheet.generalInformation")}</h2>
                        </div>

                        <TabsNavigation
                            tabs={tabsGeneral}
                            activeTab={activeTabGeneral}
                            onTabChange={setActiveTabGeneral}
                        />

                        <div className="ficha-gestor__section-content">
                            {renderGeneralContent()}
                        </div>
                    </div>

                    <div className="ficha-gestor__section">
                        <div className="ficha-gestor__section-header">
                            <h2>{t("minorWorkSheet.minorWorkData")}</h2>
                        </div>

                        <TabsNavigation
                            tabs={tabsDatos}
                            activeTab={activeTabDatos}
                            onTabChange={setActiveTabDatos}
                        />

                        <div className="ficha-gestor__section-content">
                            {renderDatosContent()}
                        </div>
                    </div>
                </div>
            )}

            {/* 9 = ObrasMenoresDocumentos en TipoValidacionTable. */}
            <ValidacionesHistorico
                isOpen={isHistoricoOpen}
                onClose={() => setIsHistoricoOpen(false)}
                codTipoValidacion={9}
                codClave={documentoHistorico?.codDocumento}
            />
        </DetalleLayout>
    );
}
